/**
 * `useFieldOfflineDrain` — устанавливает `online`-listener и стартует
 * `drainQueue` для текущего рабочего. Также делает initial drain на mount
 * (на случай если страница загрузилась с уже-онлайном после reconnect'а
 * без события `online`).
 *
 * Хук ставится на странице `/field` (FieldTimesheetPage) — единственная
 * точка, где worker-flow реально проводит время. На `/field/sign` хук НЕ
 * нужен: страница сама дренирует очередь синхронно перед sign.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { drainQueue } from "@/lib/field-offline-queue";

export function useFieldOfflineDrain(userId: number | null): void {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;

    function trigger() {
      if (cancelled) return;
      void drainQueue(userId!, queryClient, {
        onDropped: () => {
          toast.error(t("field.offline.toast.dropped"));
        },
      });
    }

    // Initial pass — если был enqueue без последующего online-event'а
    // (например, reload страницы при уже-онлайне), всё равно дренируем.
    trigger();

    window.addEventListener("online", trigger);
    return () => {
      cancelled = true;
      window.removeEventListener("online", trigger);
    };
  }, [userId, queryClient, t]);
}
